import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine,
} from "recharts";
import { Loader2, Clock, X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fmtDateGB } from "../../lib/format";

/**
 * Big drill-down chart triggered by clicking a stage's sparkline on
 * the Kanban board. Toggle between a 26- and 52-week window. Below
 * the chart, every week's transitions are listed with the project
 * that drove the average — so bottlenecks can be traced to specific
 * projects.
 */
export default function TrendHistoryModal({ stageKey, stageLabel, slaDays, onClose }) {
  const [weeks, setWeeks] = useState(26);
  const [state, setState] = useState({ loading: true, data: null });
  const [openWeek, setOpenWeek] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setState((s) => ({ ...s, loading: true }));
      try {
        const { data } = await api.get(`/pm/stage-trend/${stageKey}`, {
          params: { weeks },
        });
        if (!cancel) setState({ loading: false, data });
      } catch (e) {
        if (!cancel) {
          toast.error(formatApiError(e.response?.data?.detail) || "Could not load trend");
          setState({ loading: false, data: null });
        }
      }
    })();
    return () => { cancel = true; };
  }, [stageKey, weeks]);

  const chartData = (state.data?.weeks || []).map((w) => ({
    week: (w.week_start || "").slice(5),   // MM-DD for chart axis
    week_start: w.week_start,
    avg: w.avg_days,
    n: w.sample_size,
  }));

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="pm-trend-modal"
    >
      <div
        className="bg-white w-full max-w-4xl rounded-lg shadow-xl overflow-hidden max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b border-[var(--line)] flex items-start justify-between gap-3">
          <div>
            <div className="label mb-1">Stage trend history</div>
            <div className="font-display text-2xl font-bold">{stageLabel}</div>
            <div className="muted text-xs mt-1">
              {state.data
                ? `${state.data.total_transitions} transition${state.data.total_transitions === 1 ? "" : "s"} over the last ${weeks} weeks · overall avg ${state.data.overall_avg_days ?? "—"}d`
                : "Loading…"}
              {slaDays != null && <span> · SLA {slaDays}d</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded border border-[var(--line)] overflow-hidden" data-testid="pm-trend-window-toggle">
              {[26, 52].map((w) => (
                <button
                  key={w}
                  onClick={() => setWeeks(w)}
                  className={`px-3 py-1.5 text-xs ${weeks === w ? "bg-[var(--ink)] text-white" : "bg-white"}`}
                  data-testid={`pm-trend-window-${w}`}
                >
                  {w} weeks
                </button>
              ))}
            </div>
            <button
              onClick={onClose}
              className="btn-ghost !p-1.5"
              data-testid="pm-trend-close"
              title="Close"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {state.loading && (
            <div className="flex items-center justify-center py-16 muted text-sm gap-2" data-testid="pm-trend-loading">
              <Loader2 size={16} className="animate-spin" /> Loading history…
            </div>
          )}

          {!state.loading && state.data && (
            <>
              <div className="h-64 mb-6" data-testid="pm-trend-chart">
                <ResponsiveContainer>
                  <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: -10 }}>
                    <CartesianGrid stroke="#EEF0F6" vertical={false} />
                    <XAxis dataKey="week" tick={{ fontSize: 10, fill: "#44595A" }} />
                    <YAxis
                      tick={{ fontSize: 10, fill: "#44595A" }}
                      label={{ value: "days", angle: -90, position: "insideLeft", offset: 15, style: { fontSize: 10, fill: "#44595A" } }}
                    />
                    {slaDays != null && (
                      <ReferenceLine
                        y={slaDays}
                        stroke="#C0362C"
                        strokeDasharray="4 3"
                        label={{ value: `SLA ${slaDays}d`, fontSize: 10, fill: "#C0362C", position: "right" }}
                      />
                    )}
                    <Tooltip
                      contentStyle={{ background: "white", border: "1px solid var(--line)", fontSize: 12 }}
                      formatter={(v, _n, item) => [
                        v == null ? "—" : `${v.toFixed(1)} days`,
                        `avg (n=${item?.payload?.n || 0})`,
                      ]}
                      labelFormatter={(l, items) => fmtDateGB(items?.[0]?.payload?.week_start)}
                    />
                    <Line
                      type="monotone"
                      dataKey="avg"
                      stroke="var(--accent-hover)"
                      strokeWidth={2}
                      dot={{ r: 3, fill: "var(--accent-hover)" }}
                      activeDot={{ r: 5 }}
                      connectNulls={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Per-week drill-down list */}
              <div className="border border-[var(--line)] rounded overflow-hidden" data-testid="pm-trend-weeks-list">
                <div className="grid grid-cols-4 gap-2 px-3 py-2 bg-[var(--surface-2,#F6F7F9)] border-b border-[var(--line)] text-[10px] font-mono uppercase tracking-wider text-[#44595A]">
                  <div>Week</div>
                  <div className="text-right">Transitions</div>
                  <div className="text-right">Avg days</div>
                  <div></div>
                </div>
                <div className="divide-y divide-[var(--line-soft,#EEE)]">
                  {state.data.weeks.map((w) => {
                    const isOpen = openWeek === w.week_start;
                    const isEmpty = !w.transitions.length;
                    return (
                      <div key={w.week_start} data-testid={`pm-trend-week-row-${w.week_start}`}>
                        <button
                          onClick={() => !isEmpty && setOpenWeek(isOpen ? null : w.week_start)}
                          disabled={isEmpty}
                          className={`w-full grid grid-cols-4 gap-2 px-3 py-2 text-sm items-center text-left ${
                            isEmpty ? "opacity-50 cursor-default" : "hover:bg-[var(--surface-2,#F6F7F9)]"
                          }`}
                        >
                          <div className="text-xs font-mono">{fmtDateGB(w.week_start)}</div>
                          <div className="text-right font-mono">{w.sample_size}</div>
                          <div className="text-right font-mono" style={{ color: w.avg_days != null ? "var(--accent-hover)" : "inherit" }}>
                            {w.avg_days == null ? "—" : `${w.avg_days.toFixed(1)}d`}
                          </div>
                          <div className="text-right text-[10px] muted">
                            {isEmpty ? "" : isOpen ? "hide" : "show projects"}
                          </div>
                        </button>
                        {isOpen && !isEmpty && (
                          <div className="bg-[var(--surface-2,#F6F7F9)] px-3 py-2" data-testid={`pm-trend-week-detail-${w.week_start}`}>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-left text-[#44595A] uppercase text-[9px] tracking-wider">
                                  <th className="py-1">Project</th>
                                  <th className="py-1">Advanced to</th>
                                  <th className="py-1 text-right">Days in stage</th>
                                  <th className="py-1 text-right">On</th>
                                </tr>
                              </thead>
                              <tbody>
                                {w.transitions.map((t, i) => (
                                  <tr key={i} className="border-t border-[var(--line-soft,#EEE)]">
                                    <td className="py-1.5 font-medium">{t.project_name}</td>
                                    <td className="py-1.5 muted">{t.to_stage_label}</td>
                                    <td className="py-1.5 text-right font-mono">
                                      <span className="inline-flex items-center gap-1">
                                        <Clock size={9} /> {t.days.toFixed(1)}d
                                      </span>
                                    </td>
                                    <td className="py-1.5 text-right font-mono muted">{fmtDateGB(t.at)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
